import React from "react";

// A clean, single-page, single-column resume layout.
// Consumes the normalized resumeData shape returned by /api/resume/redesign,
// PLUS a `design` object (also returned by that same endpoint) that carries
// AI-generated colors/fonts/spacing derived from the user's free-text
// description. Content always comes from resumeData; look & feel always
// comes from design.

const DEFAULT_DESIGN = {
  primaryColor: "#002B5B",
  accentColor: "#0288D1",
  backgroundColor: "#ffffff",
  textColor: "#1f2937",
  mutedColor: "#6b7280",
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  headingCase: "uppercase",
  spacing: "normal",
};

const SPACING_SCALE = {
  compact: { page: "36px 44px", section: 16, entry: 12, gap: 10 },
  normal: { page: "48px 56px", section: 22, entry: 16, gap: 14 },
  spacious: { page: "60px 68px", section: 30, entry: 22, gap: 18 },
};

const dateRange = (start, end, current) =>
  [start, current ? "Present" : end].filter(Boolean).join(" – ");

export default function SingleColumnResumeTemplate({ data = {}, design = {}, photoUrl = "" }) {
  const d = { ...DEFAULT_DESIGN, ...design };
  const sp = SPACING_SCALE[d.spacing] || SPACING_SCALE.normal;

  const styles = {
    page: {
      width: "794px",
      minHeight: "1123px",
      background: d.backgroundColor,
      margin: "0 auto",
      padding: sp.page,
      fontFamily: d.fontFamily,
      color: d.textColor,
      boxSizing: "border-box",
    },
    name: { fontSize: 30, fontWeight: 800, color: d.primaryColor, margin: 0, letterSpacing: "-0.02em" },
    role: { fontSize: 15, fontWeight: 600, color: d.accentColor, margin: "4px 0 12px" },
    contactRow: { display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12.5, color: d.mutedColor, marginBottom: sp.section },
    hr: { border: "none", borderTop: `2px solid ${d.primaryColor}`, margin: `0 0 ${sp.section}px` },
    sectionTitle: {
      fontSize: 13, fontWeight: 800, letterSpacing: "1.5px",
      textTransform: d.headingCase === "uppercase" ? "uppercase" : "none",
      color: d.primaryColor, margin: `${sp.section}px 0 10px`, paddingBottom: 4, borderBottom: "1px solid #e5e7eb",
    },
    para: { fontSize: 13, lineHeight: 1.7, color: d.textColor, margin: 0, whiteSpace: "pre-wrap" },
    entryHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 },
    entryTitle: { fontSize: 14, fontWeight: 700, color: d.textColor },
    entryMeta: { fontSize: 12, color: d.accentColor, fontWeight: 600 },
    entrySub: { fontSize: 12.5, color: d.mutedColor, marginBottom: 6 },
    entryBlock: { marginBottom: sp.entry },
    skillsWrap: { display: "flex", flexWrap: "wrap", gap: sp.gap },
    skillTag: {
      fontSize: 12, fontWeight: 600, color: d.accentColor, background: `${d.accentColor}18`,
      border: `1px solid ${d.accentColor}55`, borderRadius: 20, padding: "5px 14px",
    },
  };

  const pd = data.personalDetails || {};
  const fullName = [pd.firstName, pd.lastName].filter(Boolean).join(" ") || "Your Name";

  return (
    <div style={styles.page} id="redesigned-resume">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
        <div>
          <h1 style={styles.name}>{fullName}</h1>
          {pd.jobTitle && <div style={styles.role}>{pd.jobTitle}</div>}
        </div>
        {photoUrl && (
          <img
            src={photoUrl}
            alt={fullName}
            style={{ width: 84, height: 84, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `3px solid ${d.accentColor}` }}
          />
        )}
      </div>
      <div style={styles.contactRow}>
        {pd.email && <span>✉ {pd.email}</span>}
        {pd.phone && <span>☎ {pd.phone}</span>}
        {pd.city && <span>📍 {pd.city}</span>}
        {pd.linkedIn && <span>🔗 {pd.linkedIn}</span>}
        {pd.website && <span>🌐 {pd.website}</span>}
      </div>
      <hr style={styles.hr} />

      {data.summary && (
        <>
          <div style={styles.sectionTitle}>Profile</div>
          <p style={styles.para}>{data.summary}</p>
        </>
      )}

      {Array.isArray(data.workExperience) && data.workExperience.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Work Experience</div>
          {data.workExperience.map((w, i) => (
            <div key={i} style={styles.entryBlock}>
              <div style={styles.entryHead}>
                <span style={styles.entryTitle}>{w.jobTitle}</span>
                <span style={styles.entryMeta}>{dateRange(w.startDate, w.endDate, w.current)}</span>
              </div>
              <div style={styles.entrySub}>{[w.employer, w.location].filter(Boolean).join(" · ")}</div>
              {w.description && <p style={styles.para}>{w.description}</p>}
            </div>
          ))}
        </>
      )}

      {Array.isArray(data.education) && data.education.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Education</div>
          {data.education.map((e, i) => (
            <div key={i} style={styles.entryBlock}>
              <div style={styles.entryHead}>
                <span style={styles.entryTitle}>{e.degree}</span>
                <span style={styles.entryMeta}>{dateRange(e.startDate, e.endDate)}</span>
              </div>
              <div style={styles.entrySub}>{[e.institution, e.location].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
        </>
      )}

      {Array.isArray(data.skills) && data.skills.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Skills</div>
          <div style={styles.skillsWrap}>
            {data.skills.map((s, i) => <span key={i} style={styles.skillTag}>{s}</span>)}
          </div>
        </>
      )}

      {Array.isArray(data.projects) && data.projects.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Projects</div>
          {data.projects.map((p, i) => (
            <div key={i} style={styles.entryBlock}>
              <div style={styles.entryTitle}>{p.name}</div>
              {p.description && <p style={styles.para}>{p.description}</p>}
            </div>
          ))}
        </>
      )}

      {Array.isArray(data.certifications) && data.certifications.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Certifications</div>
          {data.certifications.map((c, i) => (
            <div key={i} style={styles.entryHead}>
              <span style={styles.entryTitle}>{c.name}</span>
              <span style={styles.entryMeta}>{[c.provider, c.year].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
        </>
      )}

      {Array.isArray(data.languages) && data.languages.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Languages</div>
          <div style={styles.skillsWrap}>
            {data.languages.map((l, i) => <span key={i} style={styles.skillTag}>{l}</span>)}
          </div>
        </>
      )}
    </div>
  );
}